/**
 * Security Users Management Page
 *
 * Manages staff/admin users and allows direct role assignment.
 * Simplified architecture: roles are assigned directly via Firebase Custom Claims.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useHeaderActions } from '@/app/contexts/HeaderActionsContext';
import {
  securityService,
  type SecurityUser,
  type Role,
} from '@/app/services/securityService';
import { auditService } from '@/app/services';
import { logger } from '@/app/utils/logger';
import { Table, type Column } from '@/components/common/Table/index';
import { Modal, Button } from '@/components/common/Modal';
import { Badge, type BadgeVariant } from '@/components/common/Badge';
import './SecurityUsers.scss';

const SecurityUsers: React.FC = () => {
  const { setActions } = useHeaderActions();
  const [users, setUsers] = useState<SecurityUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected user for details modal
  const [selectedUser, setSelectedUser] = useState<SecurityUser | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Create User Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserDisplayName, setNewUserDisplayName] = useState('');
  const [newUserRoleId, setNewUserRoleId] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Load users and roles
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [usersResponse, rolesResponse] = await Promise.all([
        securityService.listUsers(),
        securityService.listRoles(),
      ]);
      setUsers(usersResponse.users);
      setRoles(rolesResponse.roles);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Set header actions
  useEffect(() => {
    setActions(
      <div className="header-actions">
        <button
          className="btn-refresh"
          onClick={loadData}
          disabled={isLoading}
        >
          ↻ Refresh
        </button>
        <button
          className="btn-create"
          onClick={() => setIsCreateModalOpen(true)}
        >
          + Add User
        </button>
      </div>
    );
    return () => setActions(null);
  }, [setActions, loadData, isLoading]);

  // Handle create user
  const handleCreateUser = async (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    if (!newUserEmail || !newUserRoleId) return;

    setIsCreating(true);
    setError(null);
    try {
      await securityService.createUser({
        email: newUserEmail,
        display_name: newUserDisplayName || undefined,
        role_id: newUserRoleId,
      });
      
      // Reset form and close modal
      setNewUserEmail('');
      setNewUserDisplayName('');
      setNewUserRoleId('');
      setIsCreateModalOpen(false);
      
      // Refresh list
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle view user details
  const handleViewUser = (user: SecurityUser) => {
    setSelectedUser(user);
    setSelectedRoleId(user.role || '');
    setSuccessMessage(null);
    setIsDetailsModalOpen(true);
  };

  // Handle assign role
  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRoleId) return;

    setIsAssigning(true);
    setSuccessMessage(null);
    try {
      const previousRole = selectedUser.role;
      const updatedUser = await securityService.assignRole(selectedUser.uid, selectedRoleId);
      
      // Log role assignment for audit
      auditService.log({
        action: 'role_assigned',
        category: 'configuration',
        severity: 'critical',
        resource: { resource_type: 'admin_user', resource_id: selectedUser.uid },
        context: {
          description: `Role "${selectedRoleId}" assigned to user`,
          metadata: {
            action: 'assigned',
            target_user: { uid: selectedUser.uid, email: selectedUser.email },
            role: selectedRoleId,
            previous_role: previousRole,
          },
        },
      }).catch((err) => logger.warn('Failed to log role assignment', { error: err }));
      
      setSelectedUser(updatedUser);
      setSuccessMessage(`Role "${selectedRoleId}" assigned successfully`);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign role');
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle remove role
  const handleRemoveRole = async () => {
    if (!selectedUser) return;

    if (!window.confirm('Remove role from this user? They will lose all permissions.')) {
      return;
    }

    setIsAssigning(true);
    setSuccessMessage(null);
    try {
      const previousRole = selectedUser.role;
      const updatedUser = await securityService.removeRole(selectedUser.uid);
      
      // Log role removal for audit
      auditService.log({
        action: 'role_assigned',
        category: 'configuration',
        severity: 'critical',
        resource: { resource_type: 'admin_user', resource_id: selectedUser.uid },
        context: {
          description: `Role removed from user`,
          metadata: {
            action: 'removed',
            target_user: { uid: selectedUser.uid, email: selectedUser.email },
            previous_role: previousRole,
          },
        },
      }).catch((err) => logger.warn('Failed to log role removal', { error: err }));
      
      setSelectedUser(updatedUser);
      setSelectedRoleId('');
      setSuccessMessage('Role removed successfully');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove role');
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle disable user
  const handleDisableUser = async (uid: string) => {
    if (
      !window.confirm(
        'Are you sure you want to disable this user? They will no longer be able to sign in.'
      )
    ) {
      return;
    }

    try {
      await securityService.disableUser(uid);
      await loadData();
      setIsDetailsModalOpen(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable user');
    }
  };

  // Get role badge configuration
  const getRoleBadgeConfig = (role: string | null): { label: string; variant: BadgeVariant } => {
    switch (role) {
      case 'administrator':
        return { label: 'Administrator', variant: 'warning' };
      case 'operations':
        return { label: 'Operations', variant: 'info' };
      case 'viewer':
        return { label: 'Viewer', variant: 'default' };
      default:
        return { label: 'No Role', variant: 'default' };
    }
  };

  // Table columns
  const columns: Column<SecurityUser>[] = [
    {
      key: 'email',
      header: 'Email',
      render: (user) => (
        <div className="user-email-cell">
          <span className="user-email">{user.email || 'N/A'}</span>
          {user.display_name && (
            <span className="user-name">{user.display_name}</span>
          )}
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user) => {
        const { label, variant } = getRoleBadgeConfig(user.role);
        return <Badge variant={variant} size="sm">{label}</Badge>;
      },
    },
    {
      key: 'permissions',
      header: 'Permissions',
      render: (user) => (
        <div className="permissions-preview">
          {user.permissions.length > 0 ? (
            <>
              <span className="permission-count">
                {user.permissions.length} permission{user.permissions.length !== 1 ? 's' : ''}
              </span>
              {user.permissions.includes('*') && (
                <span className="full-access-badge">Full Access</span>
              )}
            </>
          ) : (
            <span className="no-permissions">None</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user) => (
        <span className={user.disabled ? 'status-disabled' : 'status-active'}>
          {user.disabled ? 'Disabled' : 'Active'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (user) => (
        <div className="action-buttons">
          <button
            className="btn-view"
            onClick={() => handleViewUser(user)}
          >
            Manage
          </button>
        </div>
      ),
    },
  ];

  if (isLoading && users.length === 0) {
    return <div className="security-users-loading">Loading users...</div>;
  }

  return (
    <div className="security-users-page">
      {error && <div className="error-message">{error}</div>}

      <Table 
        data={users} 
        columns={columns} 
        keyExtractor={(user) => user.uid}
        loading={isLoading}
        renderMobileCard={(user) => (
          <div className="user-mobile-card">
            <div className="card-header">
              <div className="user-info">
                <span className="user-email">{user.email}</span>
                {user.display_name && <span className="user-name">{user.display_name}</span>}
              </div>
              <div className="card-actions">
                <button
                  className="btn-view"
                  onClick={() => handleViewUser(user)}
                >
                  Manage
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="card-row">
                <span className="label">Role</span>
                <span className="value">
                  {(() => {
                    const { label, variant } = getRoleBadgeConfig(user.role);
                    return <Badge variant={variant} size="sm">{label}</Badge>;
                  })()}
                </span>
              </div>
              <div className="card-row">
                <span className="label">Status</span>
                <span className={`value ${user.disabled ? 'status-disabled' : 'status-active'}`}>
                  {user.disabled ? 'Disabled' : 'Active'}
                </span>
              </div>
            </div>
          </div>
        )}
      />

      {/* User Details Modal */}
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedUser(null);
          setSuccessMessage(null);
        }}
        title="Manage User"
        footer={
          selectedUser && (
            <>
              {!selectedUser.disabled && (
                <Button
                  variant="danger"
                  onClick={() => handleDisableUser(selectedUser.uid)}
                >
                  Disable User
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedUser(null);
                  setSuccessMessage(null);
                }}
              >
                Close
              </Button>
            </>
          )
        }
      >
        {selectedUser && (
          <div className="user-details-modal">
            <div className="user-header">
              <div className="user-avatar">
                {selectedUser.email?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="user-info">
                <h3>{selectedUser.display_name || 'No Name'}</h3>
                <p className="user-email">{selectedUser.email}</p>
                <p className="user-uid">UID: {selectedUser.uid}</p>
              </div>
            </div>

            <div className="detail-section">
              <h4>Status</h4>
              <div className="status-container">
                <span className={selectedUser.disabled ? 'status-disabled' : 'status-active'}>
                  {selectedUser.disabled ? 'Disabled' : 'Active'}
                </span>
                {selectedUser.email_verified && (
                  <Badge variant="success" size="sm">Verified Email</Badge>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h4>Assign Role</h4>
              <div className="role-assignment">
                <select
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  disabled={isAssigning || selectedUser.disabled}
                  className="role-select"
                >
                  <option value="">-- Select a Role --</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name} - {role.description}
                    </option>
                  ))}
                </select>
                <button
                  className="btn-assign"
                  onClick={handleAssignRole}
                  disabled={isAssigning || !selectedRoleId || selectedUser.disabled}
                >
                  {isAssigning ? 'Assigning...' : 'Assign Role'}
                </button>
                {selectedUser.role && (
                  <button
                    className="btn-remove-role"
                    onClick={handleRemoveRole}
                    disabled={isAssigning || selectedUser.disabled}
                  >
                    Remove Role
                  </button>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h4>Current Role</h4>
              {(() => {
                const { label, variant } = getRoleBadgeConfig(selectedUser.role);
                return <Badge variant={variant}>{label}</Badge>;
              })()}
            </div>

            <div className="detail-section">
              <h4>Permissions</h4>
              <div className="permissions-list">
                {selectedUser.permissions.length > 0 ? (
                  selectedUser.permissions.map((perm, idx) => (
                    <span key={idx} className="permission-tag">
                      {perm}
                    </span>
                  ))
                ) : (
                  <span className="no-permissions">No permissions assigned</span>
                )}
              </div>
            </div>

            {successMessage && (
              <div className="success-message">{successMessage}</div>
            )}
          </div>
        )}
      </Modal>

      {/* Add User Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setNewUserEmail('');
          setNewUserDisplayName('');
          setNewUserRoleId('');
          setError(null);
        }}
        title="Provision New User"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setIsCreateModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateUser}
              disabled={isCreating || !newUserEmail || !newUserRoleId}
            >
              {isCreating ? 'Creating...' : 'Create User'}
            </Button>
          </>
        }
      >
        <form className="create-user-form" onSubmit={handleCreateUser}>
          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              id="email"
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder="user@example.com"
              required
              disabled={isCreating}
            />
          </div>
          <div className="form-group">
            <label htmlFor="displayName">Display Name</label>
            <input
              id="displayName"
              type="text"
              value={newUserDisplayName}
              onChange={(e) => setNewUserDisplayName(e.target.value)}
              placeholder="John Doe"
              disabled={isCreating}
            />
          </div>
          <div className="form-group">
            <label htmlFor="role">Initial Role *</label>
            <select
              id="role"
              value={newUserRoleId}
              onChange={(e) => setNewUserRoleId(e.target.value)}
              required
              disabled={isCreating}
            >
              <option value="">-- Select a Role --</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <p className="form-help">
              Assigning a role will grant immediate permissions once the user signs in with Google.
            </p>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SecurityUsers;
